import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Radio, Send, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'

import PageContainer from '@/common/components/PageContainer'
import Heading from '@/common/shared/heading'
import { Button } from '@/common/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/common/ui/alert'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { ScrollArea } from '@/common/ui/scroll-area'

import { ChatMessageType } from '@/schema'
import {
	MessageListEditor,
	MessageListPreview,
} from '../ChatSettingsPage/components'
import { messageContentSchema } from '../ChatSettingsPage/types'
import type { MessageContentInput } from '../ChatSettingsPage/types'
import { useMutationBroadcastMessages } from '../../api/hooks'

// ==================== Types ====================

const broadcastSchema = z.object({
	messages: z
		.array(messageContentSchema)
		.min(1, 'กรุณาเพิ่มอย่างน้อย 1 ข้อความ'),
})

type BroadcastFormData = z.infer<typeof broadcastSchema>

interface BroadcastResult {
	totalRooms: number
	successCount: number
	failedCount: number
	skippedCount: number
}

// ==================== Component ====================

export function BroadcastPage() {
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [broadcastResult, setBroadcastResult] =
		useState<BroadcastResult | null>(null)

	const broadcastMutation = useMutationBroadcastMessages()

	const form = useForm<BroadcastFormData>({
		resolver: zodResolver(broadcastSchema),
		defaultValues: {
			messages: [
				{
					id: crypto.randomUUID(),
					order: 0,
					messageType: ChatMessageType.TEXT,
				},
			],
		},
	})

	const messages = form.watch('messages')

	const handlePreviewAndConfirm = () => {
		form.handleSubmit(() => {
			setShowConfirmDialog(true)
		})()
	}

	const handleConfirmBroadcast = () => {
		const data = form.getValues()

		// Map form data to API format
		const messagesPayload = data.messages.map((msg, index) => ({
			messageType: msg.messageType,
			order: index,
			text: msg.text,
			stickerPackageId: msg.stickerPackageId,
			stickerId: msg.stickerId,
			imageUrl: msg.imageUrl,
			videoUrl: msg.videoUrl,
			videoPreviewUrl: msg.videoPreviewUrl,
			audioUrl: msg.audioUrl,
			audioDuration: msg.audioDuration,
			fileUrl: msg.fileUrl,
			fileName: msg.fileName,
			locationTitle: msg.locationTitle,
			locationAddress: msg.locationAddress,
			locationLatitude: msg.locationLatitude,
			locationLongitude: msg.locationLongitude,
			flexAltText: msg.flexAltText,
			flexContents: msg.flexContents as Record<string, never> | undefined,
		}))

		broadcastMutation.mutate(
			{ messages: messagesPayload },
			{
				onSuccess: (result) => {
					setShowConfirmDialog(false)
					setBroadcastResult(result)
					toast.success(
						`ส่งข้อความสำเร็จ ${result.successCount}/${result.totalRooms} ห้อง`,
					)
				},
				onError: (error) => {
					toast.error(`เกิดข้อผิดพลาด: ${error.message}`)
				},
			},
		)
	}

	const handleReset = () => {
		form.reset({
			messages: [
				{
					id: crypto.randomUUID(),
					order: 0,
					messageType: ChatMessageType.TEXT,
				},
			],
		})
		setBroadcastResult(null)
	}

	return (
		<PageContainer>
			<div className="mb-6">
				<Heading
					title="Broadcast ข้อความ"
					description="ส่งข้อความไปยังห้องแชททั้งหมดพร้อมกัน (รองรับหลายข้อความ)"
				/>
			</div>

			{/* Warning Alert */}
			<Alert
				variant="destructive"
				className="mb-6 border-amber-500/50 bg-amber-500/10"
			>
				<AlertTriangle className="h-4 w-4 text-amber-600" />
				<AlertTitle className="text-amber-700">คำเตือน</AlertTitle>
				<AlertDescription className="text-amber-600">
					การ Broadcast จะส่งข้อความไปยังห้องแชททุกห้องที่มี LINE Profile
					โปรดตรวจสอบข้อความให้ถูกต้องก่อนส่ง
					การส่งข้อความจำนวนมากจะใช้โควต้าข้อความของ LINE Official Account
				</AlertDescription>
			</Alert>

			{/* Result Card */}
			{broadcastResult && (
				<Card className="mb-6 border-green-500/50 bg-green-500/5">
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-green-700">
							<CheckCircle2 className="h-5 w-5" />
							ผลการ Broadcast
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
							<div className="text-center">
								<p className="text-2xl font-bold text-foreground">
									{broadcastResult.totalRooms}
								</p>
								<p className="text-sm text-muted-foreground">ห้องทั้งหมด</p>
							</div>
							<div className="text-center">
								<p className="text-2xl font-bold text-green-600">
									{broadcastResult.successCount}
								</p>
								<p className="text-sm text-muted-foreground">สำเร็จ</p>
							</div>
							<div className="text-center">
								<p className="text-2xl font-bold text-red-600">
									{broadcastResult.failedCount}
								</p>
								<p className="text-sm text-muted-foreground">ล้มเหลว</p>
							</div>
							<div className="text-center">
								<p className="text-2xl font-bold text-amber-600">
									{broadcastResult.skippedCount}
								</p>
								<p className="text-sm text-muted-foreground">ข้าม</p>
							</div>
						</div>
						<div className="mt-4 flex justify-center">
							<Button variant="outline" onClick={handleReset}>
								สร้าง Broadcast ใหม่
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Main Form */}
			{!broadcastResult && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Radio className="h-5 w-5" />
							สร้างข้อความ Broadcast
						</CardTitle>
					</CardHeader>
					<CardContent>
						<Form {...form}>
							<form className="space-y-6">
								<FormField
									control={form.control}
									name="messages"
									render={({ field }) => (
										<FormItem>
											<FormLabel>ข้อความ (สูงสุด 5 ข้อความต่อครั้ง)</FormLabel>
											<FormControl>
												<MessageListEditor
													messages={field.value}
													onChange={field.onChange}
													maxMessages={5}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<div className="flex justify-end gap-2">
									<Button type="button" variant="outline" onClick={handleReset}>
										ล้างข้อมูล
									</Button>
									<Button
										type="button"
										onClick={handlePreviewAndConfirm}
										disabled={messages.length === 0}
									>
										<Send className="mr-2 h-4 w-4" />
										ดูตัวอย่างและส่ง
									</Button>
								</div>
							</form>
						</Form>
					</CardContent>
				</Card>
			)}

			{/* Confirm Dialog */}
			<Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-amber-500" />
							ยืนยันการ Broadcast
						</DialogTitle>
						<DialogDescription>
							ข้อความจะถูกส่งไปยังห้องแชททั้งหมดที่มี LINE Profile
							กรุณาตรวจสอบข้อความให้ถูกต้องก่อนส่ง
						</DialogDescription>
					</DialogHeader>

					<ScrollArea className="max-h-[400px]">
						<div className="py-2">
							<p className="mb-3 text-sm font-medium text-muted-foreground">
								ตัวอย่างข้อความที่จะส่ง ({messages.length} ข้อความ):
							</p>
							<MessageListPreview
								messages={messages as MessageContentInput[]}
							/>
						</div>
					</ScrollArea>

					<DialogFooter className="gap-2 sm:gap-0">
						<Button
							variant="outline"
							onClick={() => setShowConfirmDialog(false)}
							disabled={broadcastMutation.isPending}
						>
							ยกเลิก
						</Button>
						<Button
							variant="destructive"
							onClick={handleConfirmBroadcast}
							disabled={broadcastMutation.isPending}
						>
							{broadcastMutation.isPending ? (
								<>
									<span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
									กำลังส่ง...
								</>
							) : (
								<>
									<Send className="mr-2 h-4 w-4" />
									ยืนยันส่ง Broadcast
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</PageContainer>
	)
}
